import {useLocalesMgr} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';

import {getIsInternalUrl} from 'coreUtils/url';
import {ICategory} from 'coreStore/data/categories/types';

type TTitlePriority = 'contentful-first' | 'category-first';

type TNavigationTargetPage =
	| {slug?: string}
	| {props?: {slug?: string}};

interface IResolvedNavigationItemInput {
	title?: string;
	targetPage?: TNavigationTargetPage;
	targetUrl?: string;
	category?: {
		items?: Array<{id?: string}>;
		type?: string;
	};
}

interface IResolvedNavigationItem {
	resolvedTitle: string;
	resolvedUrl: string | undefined;
	category: ICategory | null;
}

/**
 * Resolves title and URL for a NavigationItem entry.
 * Replaces the title/URL mutation previously done by NavigationItemVisitor.
 *
 * @param props - Raw Contentful NavigationItem fields.
 * @param titlePriority - Which source takes precedence for the title.
 *   'contentful-first' (default): title || category.name
 *   'category-first': category.name || title
 */
const useResolvedNavigationItem = (
	props: IResolvedNavigationItemInput,
	titlePriority: TTitlePriority = 'contentful-first',
): IResolvedNavigationItem => {
	const categoryId = props?.category?.items?.[0]?.id;
	const categoryResult = useCategory(categoryId);
	const localesMgr = useLocalesMgr();

	const category = categoryResult?.category ?? null;

	const resolvedTitle =
		titlePriority === 'category-first'
			? category?.name || props.title || ''
			: props.title || category?.name || '';

	let resolvedUrl: string | undefined;

	const slug = props?.targetPage?.slug;

	if (slug) {
		resolvedUrl = localesMgr.updateLinkWithLocale(slug);
	} else if (props.targetUrl) {
		resolvedUrl = getIsInternalUrl(props.targetUrl)
			? localesMgr.updateLinkWithLocale(props.targetUrl)
			: props.targetUrl;
	} else if (category) {
		resolvedUrl = categoryResult?.getCategoryUrl() ?? undefined;
	}

	return {resolvedTitle, resolvedUrl, category};
};

export default useResolvedNavigationItem;
export type {TTitlePriority, IResolvedNavigationItem, IResolvedNavigationItemInput};
